import { useState } from "react";

import { errorMessage, useSaveSshKeys } from "../../api/queries";
import type { KeyScope, RegisteredKey } from "../../api/types";
import { DEFAULT_KEY_SCOPES, KEY_SCOPES } from "../../lib/keyScopes";
import { validateSshPublicKey } from "../../lib/sshKey";
import { CopyBlock } from "../ui/CopyBlock";
import { Field } from "../ui/Field";

interface SshKeyFormProps {
  /**
   * The keys already on file, which this form adds to rather than replaces.
   *
   * `PUT /api/v1/me` replaces the whole list, so adding a key is sending these
   * plus the new one. Defaults to none, which is the case of somebody
   * registering their first key.
   */
  existingKeys?: RegisteredKey[];
  /** Called once the key is stored. */
  onSaved?: () => void;
  /** Heading, so the same form can open a workspace or prepare for one. */
  title?: string;
  /** Called when the researcher gives up on adding this key. */
  onCancel?: () => void;
}

/**
 * Ask for one more SSH public key: the line, where it may go, what it is.
 *
 * The console never sees a private key: it stores the public half and installs
 * it on the machines this key is scoped to (see `docs/AUTH.md` §4). One key
 * per machine is the habit this asks for — the private half stays on the
 * machine that made it, which is exactly why a second machine needs a second
 * key rather than a copy of the first.
 *
 * Both places are ticked by default: that is what every key on every account
 * already does, so registering a key is no more work than it was. Unticking is
 * for the key that lives somewhere more exposed than a laptop.
 *
 * Args:
 *     existingKeys: The keys already on file; the new one is appended to them.
 *     onSaved: Optional callback once the key is accepted.
 *     title: Optional heading.
 *     onCancel: Optional way out, rendered as a button when given.
 *
 * Returns:
 *     The inline key form.
 */
export function SshKeyForm({
  existingKeys = [],
  onSaved,
  title = "First, your SSH key",
  onCancel,
}: SshKeyFormProps): JSX.Element {
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState<KeyScope[]>([...DEFAULT_KEY_SCOPES]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [scopeError, setScopeError] = useState<string | undefined>(undefined);
  const save = useSaveSshKeys();

  const toggle = (scope: KeyScope, installed: boolean): void => {
    setScopeError(undefined);
    setScopes((current) =>
      KEY_SCOPES.map((choice) => choice.value).filter((candidate) =>
        candidate === scope ? installed : current.includes(candidate),
      ),
    );
  };

  const submit = (): void => {
    const problem = validateSshPublicKey(value);
    setError(problem ?? undefined);
    // Refused here for the same reason the server refuses it: a key the
    // console may install nowhere would open nothing, and nobody could say
    // what that row on the screen is for.
    const nowhere =
      scopes.length === 0
        ? "Tick at least one place. A key the console installs nowhere would open nothing."
        : undefined;
    setScopeError(nowhere);
    if (problem || nowhere) {
      return;
    }
    const key: RegisteredKey = {
      public_key: value.trim(),
      scopes,
      label: label.trim() || null,
    };
    save.mutate(
      { ssh_public_keys: [...existingKeys, key] },
      {
        onSuccess: () => {
          setValue("");
          setLabel("");
          setScopes([...DEFAULT_KEY_SCOPES]);
          onSaved?.();
        },
      },
    );
  };

  return (
    <section className="ssh-form" aria-labelledby="ssh-form-title">
      <h2 className="ssh-form__title" id="ssh-form-title">
        {title}
      </h2>
      <p className="ssh-form__text">
        This is how you get into your workspace. Paste the <em>public</em> half
        of the key made on the machine you connect from — the console never sees
        the private one, and it should never leave that machine.
      </p>

      <ol className="ssh-form__steps">
        <li>
          Print your public key:
          <CopyBlock
            value="cat ~/.ssh/id_ed25519.pub"
            label="Copy the command that prints your public key"
          />
        </li>
        <li>
          No such file? Create the key first, then run the command above:
          <CopyBlock
            value='ssh-keygen -t ed25519 -C "$USER@$(hostname)"'
            label="Copy the command that creates an SSH key"
          />
        </li>
      </ol>

      <Field
        label="Your public key"
        htmlFor="ssh-public-key"
        hint="One line, starting with ssh-ed25519 or ssh-rsa."
        error={error ?? (save.isError ? errorMessage(save.error) : undefined)}
      >
        <textarea
          id="ssh-public-key"
          className={`textarea textarea--mono${error ? " textarea--invalid" : ""}`}
          value={value}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "ssh-public-key-error" : "ssh-public-key-hint"}
          onChange={(event) => setValue(event.target.value)}
          placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA… you@laptop"
        />
      </Field>

      <fieldset className="ssh-form__scopes">
        <legend className="field__label">Where this key may be installed</legend>
        <p className="ssh-form__text">
          These keys are installed on the machines you reach: your own
          workspaces, and the machine that reaches MLflow and Jean Zay. Ticking
          a box says where the console may put this key — not what the key may
          do once it is there.
        </p>
        {KEY_SCOPES.map((scope) => (
          <label className="ssh-form__scope" key={scope.value}>
            <input
              type="checkbox"
              id={`ssh-scope-${scope.value}`}
              // The sentence beside the box explains it; the box itself is
              // named by the place, so it is announced as one short thing.
              aria-label={scope.label}
              checked={scopes.includes(scope.value)}
              onChange={(event) => toggle(scope.value, event.target.checked)}
            />
            <span>
              <span className="ssh-form__scope-name">{scope.label}</span>
              <span className="ssh-form__scope-meaning">{scope.meaning}</span>
            </span>
          </label>
        ))}
        {scopeError ? (
          <span className="field__error" role="alert">
            {scopeError}
          </span>
        ) : (
          <span className="field__hint">
            Both are ticked because that is what every key already does. You can
            change this later without retyping the key.
          </span>
        )}
      </fieldset>

      <Field
        label="What do you call this machine? (optional)"
        htmlFor="ssh-key-label"
        hint="Only useful when the key's own comment names a person rather than a machine. At most 60 characters."
      >
        <input
          id="ssh-key-label"
          className="input"
          type="text"
          maxLength={60}
          value={label}
          autoComplete="off"
          onChange={(event) => setLabel(event.target.value)}
          placeholder="the desktop under the window"
        />
      </Field>

      <div className="ssh-keys__actions">
        <button
          type="button"
          className="btn btn--primary"
          onClick={submit}
          disabled={save.isPending}
        >
          {save.isPending ? "Saving…" : "Save my key"}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </section>
  );
}
